import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { map, Observable } from 'rxjs';
import { API_URL_V4, environment } from 'src/environments/environment.local';

import {
  ActivitiesHttpResult,
  ActivityDetailsHttpResponse,
  ActivityInformationsHTTPResponse,
  SessionActivitiesResult,
} from '../models/activity.model';
import { StatSportType } from '../models/stats.model';
import { IActivityApi } from './interfaces/activity-abstract.api';
import { Top, Top3, Top3ApiResponse } from '@kinomap-dev/ng-ui-lib';

export type ExportTypes = 'gpx' | 'tcx' | 'pwx';

@Injectable({
  providedIn: 'root',
})
export class ActivityApi implements IActivityApi {
  constructor(private readonly http: HttpClient) { }

  dislike(activityId: string, sessionId: string) {
    const url = environment.api.trainingLog.activitySessionLikes
      .replace('{activityId}', activityId)
      .replace('{sessionId}', sessionId);
    return this.http.delete(url).pipe(
      map((result: any) => {
        return result;
      })
    );
  }

  getActivities(page: number, limit: number): Observable<ActivitiesHttpResult> {
    const params = new HttpParams()
      .set('page', page)
      .set('limit', limit)
      .set('includeGpsOnly', 'true')
      .set('includeWorkout', 'true')
      .set('includeFreeRide', 'true');
    const url = environment.api.dashboard.myActivitiesSessions;
    return this.http.get<ActivitiesHttpResult>(url, { params });
  }

  getActivitiesByMonth(
    month: number,
    year: number,
    sportType: StatSportType,
    limit: number = 15,
    page: number = 1
  ): Observable<SessionActivitiesResult> {
    const params = new HttpParams()
      .set('month', month)
      .set('year', year)
      .set('sportType', sportType)
      .set('includeGpsOnly', 'true')
      .set('includeWorkout', 'true')
      .set('includeFreeRide', 'true')
      .set('limit', limit)
      .set('page', page);
    const url = environment.api.dashboard.myActivitiesSessions;
    return this.http.get<SessionActivitiesResult>(url, { params });
  }

  getActivityDetails(
    activityId: string
  ): Observable<ActivityDetailsHttpResponse> {
    const url: string = environment.api.trainingLog.activityDetails.replace(
      '{activityId}',
      activityId
    );
    return this.http.get(url).pipe(
      map((result: any) => {
        return result;
      })
    );
  }

  getActivityInformations(
    activityId: string
  ): Observable<ActivityInformationsHTTPResponse> {
    const url: string =
      environment.api.trainingLog.activityInformations.replace(
        '{activityId}',
        activityId
      );
    return this.http.get<ActivityInformationsHTTPResponse>(url).pipe(
      map((result: ActivityInformationsHTTPResponse) => {
        return result;
      })
    );
  }

  getActivityRanking(
    videoId: number,
    page: number,
    ageMin: number,
    ageMax: number,
    gender: string,
    machineType: string,
    manufacturer: string
  ): Observable<Top[]> {
    const url: string = environment.api.watch.ranking;
    let params: HttpParams = new HttpParams().set('page', page);
    if (ageMax !== null && ageMin !== null)
      params = params.set('ageMin', ageMin).set('ageMax', ageMax);
    if (gender) params = params.set('sex', gender);
    if (machineType) params = params.set('equipment_type', machineType);
    if (manufacturer) params = params.set('manufacturer', manufacturer);

    return this.http.get<Top[]>(url.replace('{videoId}', videoId + ''), {
      params,
    });
  }

  getActivitySessionLikes(
    activityId: string,
    sessionId: string
  ): Observable<any> {
    const url: string = environment.api.trainingLog.activitySessionLikes
      .replace('{activityId}', activityId)
      .replace('{sessionId}', sessionId);
    return this.http.get(url).pipe(
      map((result: any) => {
        console.log(result);

        return result;
      })
    );
  }

  getActivityTop(videoId: number): Observable<Top3> {
    const url = environment.api.watch.top3;
    return this.http
      .get<Top3ApiResponse>(url.replace('{videoId}', videoId + ''))
      .pipe(map((result: any) => result.data));
  }

  getMapModeActivityTop(activityId: number | string): Observable<Top3> {
    const url = environment.api.watch.mapModeTop3;
    return this.http
      .get<Top3ApiResponse>(url.replace('{videoId}', activityId + ''))
      .pipe(map((result: any) => result.data));
  }

  getGpx(activityId: string, format: ExportTypes): Observable<any> {
    const url: string = `${API_URL_V4}/activities/${activityId}/export`;
    const params: HttpParams = new HttpParams().append('format', format);
    return this.http.get<any>(url, { responseType: 'blob' as 'json', params });
  }

  like(activityId: string, sessionId: string) {
    const url = environment.api.trainingLog.activitySessionLikes
      .replace('{activityId}', activityId)
      .replace('{sessionId}', sessionId);
    return this.http.post(url, {}).pipe(
      map((result: any) => {
        return result;
      })
    );
  }

  deleteActivity(activityId: string): Observable<any> {
    const url: string = `${API_URL_V4}/activities/{activityId}`.replace('{activityId}', activityId);
    this.http.delete(url).pipe(
      map((result: any) => {
        return result;
      })
    );
    return this.http.delete(url);

  }

  share(
    activityId: number,
    sessionId: number,
    platform:
      | 'decathlon'
      | 'email'
      | 'logbookConcept2'
      | 'nolio'
      | 'runkeeper'
      | 'runtastic'
      | 'strava'
      | 'trainingPeaks'
  ) {
    const url: string = `${environment.api.trainingLog.shareActivity.replace('{activityId}', activityId.toString()).replace('{sessionId}', sessionId.toString())}`;
  }

  updateActivityStatus(activityId: string, status: string): Observable<any> {
    const url: string = environment.api.trainingLog.updateActivity.replace(
      '{activityId}',
      activityId
    );
    return this.http.post(url, { status }).pipe(
      map((result: any) => {
        return result;
      })
    );
  }
}
